import type { ChangeEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormElement } from '@/components/FormElement';
import { FormSection } from '@/components/FormSection';
import Input from '@/components/core/Input';
import { Spinner } from '@/components/core/Spinner';
import { IconCheck } from '@/icons/CoreIcons';

interface StudentCardSerialProps {
  formattedSerial: string;
  errorMessage: string;
  isChecking: boolean;
  isClaimable: boolean;
  onChange: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

export const StudentCardSerial = ({
  formattedSerial,
  errorMessage,
  isChecking,
  isClaimable,
  onChange,
}: StudentCardSerialProps) => {
  const { formatMessage } = useIntl();

  const helperText =
    isClaimable || isChecking || errorMessage
      ? undefined
      : formatMessage({
          defaultMessage: 'Valfritt. 16 siffror, eller lämna tomt.',
        });

  return (
    <FormSection
      title={<FormattedMessage defaultMessage="Skolkort" />}
      description={
        <FormattedMessage defaultMessage="Valfritt. Ange kortnumret om eleven redan har ett blankt skolkort. Kortet måste vara giltigt och ledigt innan eleven kan sparas. Lämna fältet tomt om ett kort ska beställas automatiskt, eller om eleven är inaktiv i en annan organisation och kommer flyttas när hen läggs till här (skolkort överförs automatiskt med vid flytt)." />
      }
      editMode
    >
      <FormElement
        id="card_serial"
        label={<FormattedMessage defaultMessage="Kortnummer" />}
        input={
          <Input
            id="card_serial"
            name="card_serial"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            placeholder={formatMessage({
              defaultMessage: '1234 1234 1234 1234',
            })}
            value={formattedSerial}
            onChange={onChange}
            error={errorMessage}
            helperText={helperText}
            helperTextReserveSpace
            aria-busy={isChecking || undefined}
            aria-describedby={isClaimable ? 'card_serial-status' : undefined}
            endAdornment={
              isChecking ? (
                <Spinner className="size-4" />
              ) : isClaimable ? (
                <IconCheck
                  className="size-4 text-green-600"
                  aria-hidden="true"
                />
              ) : null
            }
          />
        }
      />
      {isClaimable && (
        <p id="card_serial-status" role="status" className="text-text-800">
          <FormattedMessage defaultMessage="Kortet är giltigt och kan kopplas till eleven." />
        </p>
      )}
    </FormSection>
  );
};
